import {graphql} from 'gatsby';
import React, {FC} from 'react';

import {HeaderMenu} from '../../../components/modules/navigation/menu/headerMenu';
import {HeaderMenu as HeaderMenuR4} from '../../../components/modules/navigation/menuR4/headerMenu';
import {HeaderMenuFragment, Maybe} from '../../../lib/queryTypes';
import {
  HeaderMenuDynamicProps,
  HeaderMenuProps
} from '../../../components/modules/navigation/menu/headerMenuTypes';
import {
  HeaderMenuDynamicProps as HeaderMenuDynamicPropsR4,
  HeaderMenuProps as HeaderMenuPropsR4
} from '../../../components/modules/navigation/menuR4/headerMenuTypes';
import {notEmpty} from '../utils';
import {DesignVersionType} from '../../../lib/styleHelpers';

import FrontPageHeader, {FrontPageHeaderDynamicProps} from './frontPageHeader';

export type HeaderProps = {
  data: Maybe<HeaderMenuFragment>;
  designVersion?: DesignVersionType;
};

type HeaderDynamicProps = HeaderProps & HeaderMenuDynamicProps;

type HeaderDynamicPropsR4 = HeaderProps & HeaderMenuDynamicPropsR4;

export const transformHrefAndName = (item?: Maybe<{href: Maybe<string>; name: Maybe<string>}>) => ({
  href: item?.href ?? '',
  name: item?.name ?? ''
});

const transformR4 = (
  item?: Maybe<{
    href: Maybe<string>;
    name: Maybe<string>;
    description: Maybe<string>;
    iconName: Maybe<string>;
    hoverBg: Maybe<string>;
  }>
) => ({
  href: item?.href ?? '',
  name: item?.name ?? '',
  description: item?.description ?? '',
  iconName: item?.iconName ?? '',
  hoverBg: item?.hoverBg ?? ''
});

const transformProps = (dynamicProps: HeaderDynamicProps): HeaderMenuProps => {
  const {data: header, isHighlighted, locationPath, mainWrapperCss, menuWrapperCss} = dynamicProps;

  return {
    primaryItem: transformHrefAndName(header?.data?.primaryItem),
    secondaryItem: transformHrefAndName(header?.data?.secondaryItem),
    rightNav: header?.data?.rightNav?.filter(notEmpty).map(transformHrefAndName) ?? [],
    leftNav:
      header?.data?.leftNav?.filter(notEmpty).map(navItem => ({
        ...transformHrefAndName(navItem),
        subMenu: {
          items:
            navItem.subMenu?.items?.filter(notEmpty).map(item => ({
              ...transformHrefAndName(item),
              iconName: item.iconName ?? '',
              featureFlag: item.featureFlag ?? undefined
            })) ?? []
        }
      })) ?? [],
    isHighlighted,
    locationPath,
    mainWrapperCss,
    menuWrapperCss
  };
};

const transformPropsR4 = (dynamicProps: HeaderDynamicPropsR4): HeaderMenuPropsR4 => {
  const {
    data: header,
    isHighlighted,
    hasAnimatedLogo,
    hasBackgroundColorAnimation,
    locationPath,
    mainWrapperCss,
    menuWrapperCss,
    isDark
  } = dynamicProps;

  return {
    primaryItem: transformHrefAndName(header?.data?.primaryItem),
    secondaryItem: transformHrefAndName(header?.data?.secondaryItem),
    rightNav: header?.data?.rightNav?.filter(notEmpty).map(transformHrefAndName) ?? [],
    leftNav:
      header?.data?.leftNav?.filter(notEmpty).map(navItem => ({
        ...transformHrefAndName(navItem),
        subMenu: {
          items:
            navItem.subMenu?.items?.filter(notEmpty).map(item => ({
              ...transformHrefAndName(item),
              items: item?.items?.map(transformR4)
            })) ?? []
        }
      })) ?? [],
    isHighlighted,
    hasAnimatedLogo: hasAnimatedLogo || false,
    hasBackgroundColorAnimation,
    locationPath,
    mainWrapperCss,
    menuWrapperCss,
    isDark
  };
};

export const Header: FC<HeaderDynamicProps & FrontPageHeaderDynamicProps> = props => {
  const designVersion = props.data?.data?.designVersion;

  if (designVersion === 'r4')
    return <HeaderMenuR4 {...transformPropsR4(props)} />;

  if (designVersion === 'r4-frontPage')
    return <FrontPageHeader {...props} />;

  return <HeaderMenu {...transformProps(props)} />;
};

export const query = graphql`
  fragment HeaderMenu on ContentfulCommonJsonData {
    data {
      leftNav {
        href
        name
        subMenu {
          items {
            href
            iconName
            name
            featureFlag {
              name
              action
              data {
                name
                iconName
                href
              }
            }
            items {
              href
              name
              description
              iconName
              hoverBg
            }
          }
        }
      }
      rightNav {
        name
        href
      }
      primaryItem {
        href
        name
      }
      secondaryItem {
        href
        name
      }
      items {
        name
        href
      }
      socials {
        href
        title
        type
      }
      designVersion
    }
  }
`;
